import { ReactNode } from 'react';
import {
  Checkbox as MantineCheckbox,
  CheckboxProps as MantineCheckboxProps,
  type FloatingPosition,
} from '@mantine/core';
import clsx from 'clsx';

import { FormDescription } from '../formfield/FormDescription';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import styles from './checkbox.module.scss';

export type CheckboxProps = {
  description?: ReactNode | ReactNode[];
  tooltipPosition?: FloatingPosition;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  /* default to 1 line visible, set to 0 to deactivate text truncation */
  labelLineClamp?: number;
} & Omit<MantineCheckboxProps, 'children' | 'onChange'>;

export function Checkbox({
  description,
  tooltipPosition = 'right',
  labelLineClamp = 1,
  onChange,
  ...props
}: CheckboxProps) {
  return (
    <>
      <MantineCheckbox
        {...props}
        onChange={(e) => onChange?.(e.target.checked)}
        classNames={{
          root: styles['root'],
          body: styles['body'],
          icon: styles['icon'],
          inner: styles['inner'],
          labelWrapper: styles['labelWrapper'],
          input: styles['input'],
          label: clsx(
            styles['label'],
            labelLineClamp === 0 ? undefined : styles['label--truncated']
          ),
        }}
        label={
          props.label ? (
            <TextWithTooltip
              tooltipProps={{
                position: tooltipPosition,
                usePortal: false,
                width: '300px',
              }}
              textProps={{
                lineClamp: labelLineClamp === 0 ? undefined : labelLineClamp,
              }}
            >
              {props.label}
            </TextWithTooltip>
          ) : undefined
        }
        icon={({ indeterminate, className, ...iconProps }) => (
          <GoogleFontIcon
            name={indeterminate ? 'check_indeterminate_small' : 'check'}
            className={clsx(styles['checkbox-icon'], className)}
            solid
            {...iconProps}
          />
        )}
      />
      <FormDescription>{description}</FormDescription>
    </>
  );
}
